/**
 * Resolves a bare slash command (e.g. "/create-skill") to its canonical
 * namespaced form (e.g. "/essentials:create-skill") before submit.
 *
 * Phase 1 scope:
 * - Only resolves when the slash is the FIRST token of the message.
 * - Returns 'ambiguous' if the bare slug matches multiple abilities — UI shows picker.
 * - Returns 'unchanged' for no-match, namespaced input, or non-leading slash.
 */

export interface SkillIndexEntry {
  ability: string;
  skill: string;
}

export type SkillIndex = SkillIndexEntry[];

export type SlashResolution =
  | { kind: "unchanged"; text: string }
  | { kind: "expanded"; text: string; ability: string; skill: string }
  | { kind: "ambiguous"; candidates: SkillIndexEntry[]; original: string };

const LEADING_SLASH_RE = /^\/([a-z0-9-]+)(?::([a-z0-9-]+))?(\s|$)/i;

export function resolveSlash(text: string, index: SkillIndex): SlashResolution {
  const m = LEADING_SLASH_RE.exec(text);
  if (!m) return { kind: "unchanged", text };

  const first = m[1];
  const second = m[2];

  if (second) {
    // Already namespaced — pass through
    return { kind: "unchanged", text };
  }

  const matches = index.filter((e) => e.skill === first);
  if (matches.length === 0) return { kind: "unchanged", text };
  if (matches.length > 1)
    return { kind: "ambiguous", candidates: matches, original: text };

  const { ability, skill } = matches[0];
  const expanded = text.replace(
    LEADING_SLASH_RE,
    (_full, _g1, _g2, trailing: string) => `/${ability}:${skill}${trailing}`
  );
  return { kind: "expanded", text: expanded, ability, skill };
}
